import React, { useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import routesfe from "../../Config/RoutesFE"
import Logger from "../../utils/Logger"
import { Auth } from 'aws-amplify'
import { clearAllPaymentStorage } from "../../utils/PaymentStorageUtils";

const PaymentSuccessPage = ({ onUserUpdate }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { paymentIntent, order, plan } = location.state || {}

  useEffect(() => {
    // If no payment data, redirect to subscription page
    if (!paymentIntent || !order || !plan) {
      Logger.warn("🚫 Missing payment data, redirecting to subscription page")
      // Clear payment storage when redirecting due to missing data
      clearAllPaymentStorage();
      navigate(routesfe.APP_SUBSCRIPTION_HOME, {
        replace: true,
        state: { from: routesfe.PAYMENT_SUCCESS }
      });
    } else {
      Logger.info("✅ Payment success page loaded", {
        orderNumber: order.orderNumber,
        paymentId: paymentIntent.id,
        planName: plan.name
      });
      
      // Clear all payment-related storage after successful payment
      clearAllPaymentStorage();
      
      // Update subscription status immediately after payment success
      (async () => {
        try {
          const user = await Auth.currentAuthenticatedUser();
          await Auth.updateUserAttributes(user, {
            'custom:is_subscribed': '1'
          });
          // Fetch latest user and update app state
          const updatedUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
          if (onUserUpdate) onUserUpdate(updatedUser);
        } catch (err) {
          Logger.error('Failed to update subscription status:', err);
        }
      })();
    }
  }, [paymentIntent, order, plan, navigate, onUserUpdate])

  if (!paymentIntent || !order || !plan) {
    return null
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 flex items-center justify-center p-4">
      {/* Animated Background Elements - NOTEFORGE Purple Theme */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-90 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-4000"></div>
      </div>

      {/* Success Card */}
      <div className="relative max-w-md w-full">
        {/* NOTEFORGE Branding Header */}
        <div className="text-center mb-3">
          <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-xl rounded-xl px-4 py-2 shadow-lg border border-white/20">
            <div className="w-6 h-6 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-md flex items-center justify-center">
              <span className="text-white font-bold text-xs">N</span>
            </div>
            <span className="font-bold text-gray-900 text-base">NOTEFORGE</span>
          </div>
        </div>

        {/* Main Success Card - Compact */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 overflow-hidden">
          {/* Header with Success Animation - Reduced Height */}
          <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 px-4 py-6 text-center relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-700 via-indigo-700 to-blue-700 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
            
            {/* Compact Success Icon */}
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-white rounded-full shadow-lg mb-3 animate-bounce">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h1 className="text-xl font-bold text-white mb-1">
                Payment Successful!
              </h1>
              <p className="text-purple-100 text-sm">
                Welcome to <span className="font-semibold">{plan.name}</span>
              </p>
            </div>
          </div>

          {/* Compact Content */}
          <div className="px-4 py-4">
            {/* Order Details - Minimal Height */}
            <div className="space-y-3 mb-4">
              <div className="bg-purple-50/50 rounded-xl p-3 border border-purple-100/50">
                <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                  <div className="w-4 h-4 bg-purple-100 rounded-full flex items-center justify-center mr-2">
                    <svg className="w-2.5 h-2.5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  Order Details
                </h3>
                
                <div className="space-y-1.5">
                  {/* Order Number - Minimal Height */}
                  <div className="flex justify-between items-center py-1.5 border-b border-purple-100">
                    <span className="text-gray-600 font-medium text-xs">Order Number:</span>
                    <span className="text-gray-900 font-mono text-xs">
                      {order.orderNumber}
                    </span>
                  </div>
                  
                  {/* Amount Paid - Minimal Height */}
                  <div className="flex justify-between items-center py-1.5 border-b border-purple-100">
                    <span className="text-gray-600 font-medium text-xs">Amount Paid:</span>
                    <span className="text-base font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                      {plan.currencySymbol}{order.amount}
                    </span>
                  </div>
                  
                  {/* Payment ID - Minimal Height */}
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-gray-600 font-medium text-xs">Payment ID:</span>
                    <span className="text-gray-900 font-mono text-xs">
                      {paymentIntent.id}
                    </span>
                  </div>
                  {/* Payment Method */}
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-gray-600 font-medium text-xs">Payment Method:</span>
                    <div className="flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-indigo-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 10h18v7a3 3 0 01-3 3H6a3 3 0 01-3-3v-7zm18-3a3 3 0 00-3-3H6a3 3 0 00-3 3v1h18V7z"/>
                      </svg>
                      <span className="text-xs font-semibold text-gray-900">Card</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Success Messages - Compact */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-4 h-4 bg-green-100 rounded-full flex items-center justify-center mr-2 mt-0.5">
                    <svg className="w-2.5 h-2.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-green-800">
                    <p className="font-semibold mb-0.5 text-xs">We are activating your subscription!</p>
                    <p className="text-xs text-green-700">You'll receive an email once activated.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Compact Action Button */}
            <button
              onClick={async () => {
                try {
                  // Only fetch latest user attributes, do not update again
                  const updatedUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
                  if (onUserUpdate) onUserUpdate(updatedUser);
                } catch (err) {
                  Logger.error('Failed to refresh user attributes:', err);
                }
                navigate(routesfe.HOME);
              }}
              className="w-full bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 hover:from-purple-700 hover:via-indigo-700 hover:to-blue-700 text-white font-semibold py-2.5 px-4 rounded-2xl transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 shadow-lg group overflow-hidden relative"
            >
              {/* Shimmer Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              
              <span className="flex items-center justify-center relative z-10 text-sm">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Go to Home
              </span>
            </button>
          </div>
        </div>

        {/* Minimal Confetti Effect */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
          <div className="absolute top-8 left-8 w-1.5 h-1.5 bg-purple-400 rounded-full animate-ping opacity-75"></div>
          <div className="absolute top-12 right-6 w-1 h-1 bg-indigo-400 rounded-full animate-ping animation-delay-1000 opacity-75"></div>
          <div className="absolute bottom-16 left-12 w-1 h-1 bg-blue-400 rounded-full animate-ping animation-delay-2000 opacity-75"></div>
          <div className="absolute bottom-20 right-8 w-0.5 h-0.5 bg-purple-300 rounded-full animate-ping animation-delay-3000 opacity-75"></div>
        </div>
      </div>
    </div>
  )
}

export default PaymentSuccessPage
